import { IconButton, styled } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { useCollection, useIO, useLinked, useModel } from "@type-r/react";
import { Button, Column, Columns, Flex, Loading, PrimaryButton, div, navigate, span } from "lqx-react";
import React, { useContext, useEffect } from "react";
import { syncAssetEventsToAssets } from "../api/assetChange";
import { AssetEvent, getAssetEvents } from "../api/assetEvent";
import { ExternalSystem } from "../api/externalSystemRequest";
import { IngestionJob } from "../api/ingestionJob";
import { FileViewer } from "../components/file-viewer/file-viewer";
import { DigitizationSelect } from "../components/select";
import { SignalRContext } from "../page-router";
import { CancelButton, ContinueButton, RightAlignedButtons, StartOverButton, ViewImportJobButton, WorkflowBody, WorkflowColumn, WorkflowFooter, WorkflowTitle, downloadOriginalFile } from "../shared";
import { AssetStatus, AssetsTable, Colors } from "./components/assets-table";
import { EditAssetForm } from "./edit-asset-form";
import { Can } from "src/app/services/can";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";

const isKnownFileExtension = fileName =>
    fileName.toLowerCase().endsWith('.pdf') ||
    fileName.toLowerCase().endsWith('.csv') ||
    fileName.toLowerCase().endsWith('.xls') ||
    fileName.toLowerCase().endsWith('.xlsx') ||
    fileName.toLowerCase().endsWith('.msg') ||
    fileName.toLowerCase().endsWith('.tif');

export const ConfirmAssetsDataStep = (
    { assetName, assetType, assetModel, countModel, queryModel, tableColumns, assetChanges,
        job, next, cancel, startOver, assetForm, isEdit, isAssetsModification, externalSystems,
        $assetTypeSelect, assetTypeMultiOptions, $refreshEvents }:
        {
            assetName: String, assetType: Number, assetModel, countModel, queryModel, tableColumns,
            assetChanges: any, job: IngestionJob, next?, cancel?, startOver?, assetForm?,
            isEdit?: Boolean, isAssetsModification?: any, externalSystems: ExternalSystem[],
            $assetTypeSelect?, assetTypeMultiOptions?, $refreshEvents?
        }) => {
    const { notificationFunctions } = useContext(SignalRContext);
    const incomingAssetEvents = useCollection.of(AssetEvent);

    const selfPollAssetEvents = async () =>
        await getAssetEvents(assetChanges.items, incomingAssetEvents);


    const selfSyncAssetEventsToAssets = () =>
        syncAssetEventsToAssets(assetChanges.items, incomingAssetEvents, externalSystems, $refreshEvents);

    const $activeAssetChange = useLinked(null);
    const $isDeletedId = useLinked(null);
    const $isFileReaderVisible = useLinked(false);
    const $doModifyExistingAssetsJob = useLinked(false);
    const $filter = useLinked<string>(null).onChange(() => $activeAssetChange.set(null));
    const ingestionJob = useModel(IngestionJob);
    const $countSelectedRows = useLinked(0);
    const $selectAllChecked = useLinked(false);
    const $selectedAssets = useLinked<number[]>([]);

    useIO(selfPollAssetEvents, [assetChanges['_changeToken'], ingestionJob['_changeToken']]);
    useIO(() => countModel.fetch({ params: { jobId: job.id } }).catch(e => console.error(e)), [assetChanges['_changeToken']]);
    useEffect(selfSyncAssetEventsToAssets, [incomingAssetEvents['_changeToken']]);

    const $doStoreValidJob = useLinked(false);
    const $doDeleteAssetsJob = useLinked(false);
    const isStoreValidJobReady = useIO(
        async () => $doStoreValidJob.isTruthy && await storeValidJob(),
        [$doStoreValidJob.value]
    );

    const isBulkDeleteJobReady = useIO(
        async () => {
            if ($doDeleteAssetsJob.isTruthy) {
                $selectAllChecked.value ?
                    await ingestionJob.doDeleteAllAssets(job.importJobId, assetType) :
                    await ingestionJob.doDeleteBulkAssets($selectedAssets.value);
                $countSelectedRows.set(0);
                $doDeleteAssetsJob.set(false);
                $activeAssetChange.set(null)
            }
        },
        [$doDeleteAssetsJob.value]);

    const isModifyExistingAssetsInJobReady = useIO(
        async () => $doModifyExistingAssetsJob.isTruthy && await modifyExistingAssetsInJob(),
        [$doModifyExistingAssetsJob.value]
    );

    const modifyExistingAssetsInJob = async () => {
        try {
            ingestionJob.id = job.id;
            await ingestionJob.runModifyExistingAssets({ params: { assetType: assetType } });
            isAssetsModification.set(true)
            next();
        }
        finally {
            $doModifyExistingAssetsJob.set(false);
        }
    }

    const isReady = useIO(async () => {
        ingestionJob.id = job.id;
        await ingestionJob.fetch().catch(e => console.error(e));
        await countModel.fetch({ params: { jobId: job.id } }).catch(e => console.error(e));
    }, [$assetTypeSelect ? $assetTypeSelect['_changeToken'] : null]);

    const storeValidJob = async () => {
        try {
            ingestionJob.id = job.id;
            notificationFunctions.current.setTimerNotificationAlert('digitized assets');
            await ingestionJob.saveValid({ params: { assetType: assetType } });
            next();
        }
        finally {
            $doStoreValidJob.set(false);
        }
    }

    const toggleFileViwer = () => $isFileReaderVisible.set(!$isFileReaderVisible.value);
    const hasSourceFile = () => ingestionJob.fileName && isKnownFileExtension(ingestionJob.fileName);
    const ItemsFromTotalInfo = ({ value, total }) => <span><ValueLabel>{value}</ValueLabel><TotalLabel> / {total}</TotalLabel></span>
    const isSupportedFile = () => hasSourceFile() && (ingestionJob.fileName.toLowerCase().endsWith('.pdf') || ingestionJob.fileName.toLowerCase().endsWith('.msg') || ingestionJob.fileName.toLowerCase().endsWith('.tif'));

    const deleteFromTable = (id) => {
        $activeAssetChange.set(null);
        $isDeletedId.set(id);
        $countSelectedRows.set(0);
    }

    const $doAssetUpdated = useLinked(false);

    return isReady && isStoreValidJobReady && isModifyExistingAssetsInJobReady && isBulkDeleteJobReady
        ? <PageContainer>
            <WorkflowTitle>
                Step 3: Let's confirm your data - {ingestionJob.importJobId}
            </WorkflowTitle>
            <Summary>
                <SummaryCard color={Colors.Green}>
                    <CardSection horizontal>
                        <CardSection active={$filter.value === AssetStatus.PassValidationNewAsset} onClick={() => $filter.set(AssetStatus.PassValidationNewAsset)}>
                            <ItemsFromTotalInfo value={countModel.passValidationNewAsset} total={countModel.total} />
                            <span>Pass Validation</span>
                        </CardSection>
                        <Divider />
                        <CardSection active={$filter.value === AssetStatus.PassValidationModifiedAsset} onClick={() => $filter.set(AssetStatus.PassValidationModifiedAsset)}>
                            <ItemsFromTotalInfo value={countModel.passValidationModifiedAsset} total={countModel.total} />
                            <span>Modified</span>
                        </CardSection>
                        <Divider />
                        <CardSection active={$filter.value === AssetStatus.Digitized} onClick={() => $filter.set(AssetStatus.Digitized)}>
                            <ItemsFromTotalInfo value={countModel.digitized} total={countModel.total} />
                            <span>Digitized</span>
                        </CardSection>
                    </CardSection>
                </SummaryCard>
                <SummaryCard color={Colors.Orange}>
                    <CardSection active={$filter.value === AssetStatus.RequireReview} onClick={() => $filter.set(AssetStatus.RequireReview)}>
                        <ItemsFromTotalInfo value={countModel.requireReview + countModel.conflict} total={countModel.total} />
                        <span>Require Review</span>
                    </CardSection>
                </SummaryCard>
                <SummaryCard color={Colors.Yellow}>
                    <CardSection horizontal>
                        <CardSection active={$filter.value === AssetStatus.Duplicated} onClick={() => $filter.set(AssetStatus.Duplicated)}>
                            <ItemsFromTotalInfo value={countModel.duplicated} total={countModel.total} />
                            <span>Duplicate</span>
                        </CardSection>
                        <Divider />
                        <CardSection active={$filter.value === AssetStatus.ModifiesExistingAsset} onClick={() => $filter.set(AssetStatus.ModifiesExistingAsset)}>
                            <ItemsFromTotalInfo value={countModel.modifyExistingAssets} total={countModel.total} />
                            <span>Modifies Existing</span>
                        </CardSection>
                    </CardSection>
                </SummaryCard>
            </Summary>
            <TableHeader>
                <TableTitle>You've uploaded {countModel.total} {assetName}s</TableTitle>
                {$assetTypeSelect && <DigiSelectContainer><DigitizationSelect placeholder={'Asset Type'} $value={$assetTypeSelect} options={assetTypeMultiOptions} isMulti={false} /></DigiSelectContainer>}
                {isSupportedFile() && <FileViewerButton className={$isFileReaderVisible.isTruthy ? 'active' : ''} size="small" onClick={() => toggleFileViwer()}>Toggle File Reader</FileViewerButton>}
                {hasSourceFile() && <FileViewerButton size="small" onClick={() => downloadOriginalFile(`/api/v1/ingest/jobs/${job.id}/file`, ingestionJob.fileName)}>Download Original File</FileViewerButton>}
                {$filter && $filter.value && <FileViewerButton size="small" onClick={() => $filter.set(null)}>Clear All Filters</FileViewerButton>}
            </TableHeader>
            <StyledWorkflowBody>
                {$isFileReaderVisible.value ?
                    <FileViewerContainer>
                        <FileViewer fileName={ingestionJob.fileName} url={`/api/v1/ingest/jobs/${job.id}/file`} downloadFile={downloadOriginalFile} />
                    </FileViewerContainer>
                    : <AssetsTable
                        jobId={job.id}
                        items={assetChanges as any}
                        $active={$activeAssetChange}
                        $doDeleteAssetsJob={$doDeleteAssetsJob}
                        $filter={$filter}
                        columns={tableColumns}
                        countModel={countModel}
                        querry={queryModel}
                        $doAssetUpdated={$doAssetUpdated}
                        $countSelectedRows={$countSelectedRows}
                        $selectAllChecked={$selectAllChecked}
                        $selectedAssets={$selectedAssets}
                        $isDeletedId={$isDeletedId}
                    />
                }
                {$activeAssetChange.value &&
                    <EditAssetColumn>
                        <EditAssetForm
                            assetModel={assetModel}
                            assetChange={$activeAssetChange.value}
                            onAssetDeleted={() => deleteFromTable($activeAssetChange.value.id)}
                            $doAssetUpdated={$doAssetUpdated}
                            assetForm={asset => assetForm(asset)}
                            actions={
                                <CloseIconButton aria-label="Close" size="small" onClick={() => $activeAssetChange.set(null)}>
                                    <CloseIcon />
                                </CloseIconButton>
                            } />
                    </EditAssetColumn>
                }
            </StyledWorkflowBody>
            <WorkflowFooter>
                <Columns>
                    <StyledColumn>
                        <Flex row>
                            <ViewImportJobButton color="primary" onClick={() => navigate('import-jobs')} >View Import Jobs</ViewImportJobButton>
                        </Flex>
                    </StyledColumn>
                    <StyledColumn>
                        <RightAlignedButtons>
                            {
                                Can(UserEntitlementCatalogs.DeleteJobAssets) &&
                                <ConfirmationButton
                                    qa-id='delete not digitized'
                                    disabled={$countSelectedRows.value <= 0}
                                    onClick={$doDeleteAssetsJob.true}
                                >
                                    Delete {$countSelectedRows.value} {assetName}s
                                </ConfirmationButton>
                            }

                            {
                                Can(UserEntitlementCatalogs.DigitizeJobAssets) &&
                                <ConfirmationButton
                                    qa-id={`digitize-${assetName.toLocaleLowerCase()}s`}
                                    disabled={countModel.passValidationNewAsset + countModel.passValidationModifiedAsset <= 0}
                                    onClick={$doStoreValidJob.true}
                                >
                                    Digitize {countModel.passValidationNewAsset + countModel.passValidationModifiedAsset} {assetName}s that Pass Validation
                                </ConfirmationButton>
                            }

                            {
                                Can(UserEntitlementCatalogs.ModifyJobAssets) &&
                                    countModel.modifyExistingAssets > 0 ?
                                    <YellowConfirmationButton onClick={$doModifyExistingAssetsJob.true}>
                                        Modify {countModel.modifyExistingAssets} Existing {assetName}{countModel.modifyExistingAssets > 1 ? "s" : ""}
                                    </YellowConfirmationButton> : <></>
                            }
                            {isEdit || (isAssetsModification && isAssetsModification.value) ? <ContinueButton color="primary" onClick={next}>Continue</ContinueButton> : <></>}
                        </RightAlignedButtons>
                    </StyledColumn>
                </Columns>
            </WorkflowFooter>
        </PageContainer> : <Loading />
};

export const PageContainer = div({
    display: 'grid',
    gridTemplateRows: 'auto auto auto 1fr auto',
    height: 'calc(100% - 190px)'
});

const StyledColumn = styled(Column)({
    flex: '1 1 auto'
});
export const StyledWorkflowBody = styled(WorkflowBody)({
    height: '100%',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: 0,
    marginTop: 0
});

const FileViewerContainer = div({
    background: '#FFFFFF',
    border: "1px solid #00000030",
    width: '100%',
    overflow: 'auto'
});

const FileViewerButton = styled(Button)({
    margin: '8px',
    borderRadius: '2px',
    fontSize: '11px',
    '&.active': {
        background: '#3a4b60',
        color: 'white'
    }
});

const DigiSelectContainer = div({
    width: '250px',
    margin: '8px',
    borderRadius: '2px',
    fontSize: '11px',
    '& div': {
        '& [class*="-control"]': {
            minHeight: '24px',
            height: '24px',
            '& > *': {
                minHeight: '24px',
                height: '24px',
            }
        }
    }
});

const CloseIconButton = styled(IconButton)({
    display: 'block',
    color: '#052C4C',
    marginLeft: 'auto',
    marginRight: '10px',
});

export const AssetsSectionColumn = styled(WorkflowColumn)({
});

export const EditAssetColumn = div({
    flex: '0 0 50%',
    display: 'flex',
    flexDirection: 'column'
});

export const NavigationButton = styled(Button)({
    marginBottom: '10px',
    backgroundColor: '#4197d6',
    border: '0px',
    color: 'white',
    borderRadius: '10px',
    height: '25px',
    fontSize: '14px',
    marginLeft: 'auto',
    '&:hover': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        fontSize: '14px',
        marginLeft: 'auto'
    },
    '&:active': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        fontSize: '14px',
        marginLeft: 'auto'
    },
    '&:focus': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        fontSize: '14px',
        marginLeft: 'auto'
    }
});

export const FileViewerColumn = styled(WorkflowColumn)({
    minWidth: '800px',
    margin: '2px',
});

export const FileViewerTitle = div({
    marginLeft: '20px'
});

const ConfirmationButton = styled(PrimaryButton)({
    marginBottom: '10px',
    border: '1px',
    color: 'white',
    borderRadius: '10px',
    minWidth: '200px',
    minHeight: '25px'
});

const YellowConfirmationButton = styled(PrimaryButton)({
    marginBottom: '10px',
    border: '1px',
    color: 'white',
    borderRadius: '10px',
    minWidth: '200px',
    minHeight: '25px',
    backgroundColor: '#f2b824',
    '&:hover': {
        backgroundColor: '#f2b824',
        fontSize: '14px !important',
        fontWeight: 600,
    },
});

export const ValueLabel = span({
    fontWeight: 600,
    fontSize: 'larger'
});

export const TotalLabel = span({
    fontWeight: 500,
    fontSize: 'small',
    color: '#a6a6a6'
});

export const Summary = div({
    display: 'flex',
    gridGap: 12,
    justifyContent: 'space-around'
});

export const SummaryCard = div({
    maxWidth: 555,
    width: '100%',
    margin: '1.5vh',
    background: 'white',
    borderRadius: 12,
    boxShadow: '2px 4px 6px rgb(0 0 0 / 16%), 2px 4px 6px rgb(0 0 0 / 23%)',
    alignContent: 'center',
    justifyItems: 'center',
    display: 'grid',
    padding: "2vh",
    border: "1px solid #00000030",
    borderBottom: (props: any) => `8px solid ${props.color || 'white'}`
});

export const CardSection = div({
    display: 'flex',
    alignItems: 'center',
    flexDirection: (props: any) => props.horizontal ? 'row' : 'column',
    width: '100%',
    justifyItems: 'center',
    gridGap: 4,
    userSelect: 'none',
    color: (props: any) => props.active ? '#052C4C' : '#5f839e',
    fontWeight: (props: any) => props.active ? 600 : 500,
    fontSize: 'clamp(12px, 0.9vw, 18px)',
    textAlign: 'center',
    padding: "4px 8px",
    whiteSpace: 'nowrap',
    '&:hover': {
        fontWeight: 600,
        cursor: 'pointer',
        fontSize: 'calc(clamp(12px, 0.9vw, 18px) - 0.02vw)'
    },
    '&.active': {
        fontWeight: 600
    },
    '&:active': {
        fontWeight: 500
    }
});

export const Divider = div({
    flex: '1 1 1px',
    width: '100%',
    border: '1px solid #ececec',
    height: '100%'
});

export const GridContent = div({
    display: 'flex',
    flexDirection: 'row'
});

export const TableTitle = div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '0 10px',
    fontWeight: 400,
    color: 'white',
    fontSize: '11pt'
});

export const TableHeader = div({
    display: 'flex',
    flexDirection: 'row',
    background: '#052C4C',
    height: 40
});
